import React from 'react';
import { Rocket, Shield, Box, Zap, Settings2, Database, Palette, Volume2, Maximize2, Info } from 'lucide-react';
import { SimulationConfig, ShapeType } from '@/types';
import { Toggle } from './Toggle';
import { SmartInput } from './SmartInput';
import { ControlGroup } from './ControlGroup';
import { SHAPES, BALL_SOUND_EFFECTS } from '@/constants/constants';
import { SETTING_INFO } from '@/data/settingInfo';
import { InfoPopup } from './InfoPopup';
import { CollapsibleSection } from './CollapsibleSection';

interface SpaceSpiralSettingsSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
  isLocked?: boolean;
  minPlan?: string;
  getLockInfo?: (id: string) => { isLocked: boolean; min_plan?: string };
  onUpgradeClick?: () => void;
}

export const SpaceSpiralSettingsSection: React.FC<SpaceSpiralSettingsSectionProps> = ({ 
  config, 
  setConfig,
  isLocked,
  minPlan,
  getLockInfo,
  onUpgradeClick
}) => {
  const isLockedFn = (id: string) => getLockInfo?.(id).isLocked || false;
  const isSpaceSpiral = config.mode === 'SPACE_SPIRAL';
  const spaceSpiralMsg = "This setting is designed specifically for SPACE SPIRAL mode.";

  return (
    <CollapsibleSection 
        title="Space Spiral Settings" 
        icon={Rocket} 
        darkMode={config.darkMode}
        isLocked={isLocked}
        lockRequiredPlan={minPlan as any}
        onUpgradeClick={onUpgradeClick}
        defaultOpen
        settingKey="spaceSpiralStudio"
    >
      <div className={`w-full flex flex-col gap-6 mt-4 transition-all duration-500 ${isLocked ? 'opacity-50 grayscale pointer-events-none' : ''}`}>
        
        {/* PHYSICAL CONSTANTS */}
        <div className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
                <Zap className="w-3.5 h-3.5 opacity-20" />
                <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">Spiral Physics</h4>
                <InfoPopup settingKey="spaceSpiralPhysics" darkMode={config.darkMode} />
            </div>
            
            <div className="flex flex-col gap-4">
                <ControlGroup label="Ball Gravity" settingKey="spaceSpiralGravity" disabled={!isSpaceSpiral} disabledMessage={spaceSpiralMsg} modes={['SPACE_SPIRAL']} darkMode={config.darkMode}>
                    <Toggle active={config.spaceSpiralGravity} onClick={() => setConfig(p => ({...p, spaceSpiralGravity: !p.spaceSpiralGravity}))} />
                </ControlGroup>

                <div className="grid grid-cols-2 gap-4">
                    <SmartInput label="Gravity Power" settingKey="spaceSpiralGravityPower" min={0} max={5} step={0.1} value={config.spaceSpiralGravityPower || 1.0} onChange={v => setConfig(p => ({...p, spaceSpiralGravityPower: v}))} darkMode={config.darkMode} disabled={!isSpaceSpiral} disabledMessage={spaceSpiralMsg} modes={['SPACE_SPIRAL']} />
                    <SmartInput label="Forward Thrust" settingKey="spaceSpiralForwardThrust" min={0} max={50} step={0.5} value={config.spaceSpiralForwardThrust || 0} onChange={v => setConfig(p => ({...p, spaceSpiralForwardThrust: v}))} darkMode={config.darkMode} disabled={!isSpaceSpiral} disabledMessage={spaceSpiralMsg} modes={['SPACE_SPIRAL']} />
                </div>
            </div>
        </div>

        {/* GEOMETRY ENGINE */}
        <div className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
                <Box className="w-3.5 h-3.5 opacity-20" />
                <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">Geometry Engine</h4>
                <InfoPopup settingKey="spaceSpiralGeometry" darkMode={config.darkMode} />
            </div>
            
            <div className="flex flex-col gap-4">
                <SmartInput label="Box Count" settingKey="spaceSpiralBoxCount" min={10} max={500} value={config.spaceSpiralBoxCount} onChange={v => setConfig(p => ({...p, spaceSpiralBoxCount: v}))} darkMode={config.darkMode} disabled={!isSpaceSpiral} disabledMessage={spaceSpiralMsg} modes={['SPACE_SPIRAL']} />
                
                <div className="grid grid-cols-2 gap-4">
                    <SmartInput label="Box Width" settingKey="spaceSpiralBoxWidth" min={5} max={100} value={config.spaceSpiralBoxWidth || config.spaceSpiralBoxSize} onChange={v => setConfig(p => ({...p, spaceSpiralBoxWidth: v}))} darkMode={config.darkMode} disabled={!isSpaceSpiral} disabledMessage={spaceSpiralMsg} modes={['SPACE_SPIRAL']} />
                    <SmartInput label="Box Height" settingKey="spaceSpiralBoxHeight" min={5} max={100} value={config.spaceSpiralBoxHeight || config.spaceSpiralBoxSize} onChange={v => setConfig(p => ({...p, spaceSpiralBoxHeight: v}))} darkMode={config.darkMode} disabled={!isSpaceSpiral} disabledMessage={spaceSpiralMsg} modes={['SPACE_SPIRAL']} />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <SmartInput label="Min Box Scale" settingKey="spaceSpiralMinBoxScale" min={0.1} max={1.0} step={0.05} value={config.spaceSpiralMinBoxScale || 0.3} onChange={v => setConfig(p => ({...p, spaceSpiralMinBoxScale: v}))} darkMode={config.darkMode} disabled={!isSpaceSpiral} disabledMessage={spaceSpiralMsg} modes={['SPACE_SPIRAL']} />
                    <SmartInput label="Max Box Scale" settingKey="spaceSpiralMaxBoxScale" min={0.1} max={2.0} step={0.05} value={config.spaceSpiralMaxBoxScale || 1.0} onChange={v => setConfig(p => ({...p, spaceSpiralMaxBoxScale: v}))} darkMode={config.darkMode} disabled={!isSpaceSpiral} disabledMessage={spaceSpiralMsg} modes={['SPACE_SPIRAL']} />
                </div>

                <SmartInput label="Spiral Scale" settingKey="spaceSpiralScale" min={0.1} max={3.0} step={0.1} value={config.spaceSpiralScale || 1.0} onChange={v => setConfig(p => ({...p, spaceSpiralScale: v}))} darkMode={config.darkMode} disabled={!isSpaceSpiral} disabledMessage={spaceSpiralMsg} modes={['SPACE_SPIRAL']} />
            </div>
        </div>

        {/* HERO BALL IDENTITY */}
        <div className="bg-blue-500/10 border border-blue-500/20 p-5 rounded-3xl space-y-3">
            <div className="flex items-center gap-3">
                <Maximize2 className="w-4 h-4 text-blue-400" />
                <span className="text-[10px] font-black uppercase tracking-widest text-blue-400">Hero Ball Profile</span>
            </div>
            <p className="text-[9px] font-medium opacity-60 leading-relaxed">
                The hero ball in this mode is fully synchronized with your <span className="font-bold border-b border-white/20">Universal Ball Settings</span>. 
                Any changes made to style, texture, or glow in the main Ball tab will be applied here automatically.
            </p>
        </div>

        {/* ART STYLE - BOXES */}
        <div className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
                <Palette className="w-3.5 h-3.5 opacity-20" />
                <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">Box Aesthetics</h4>
                <InfoPopup settingKey="spaceSpiralAesthetics" darkMode={config.darkMode} />
            </div>

            <div className="flex flex-col gap-4">
                <div className="grid grid-cols-2 gap-4">
                    <ControlGroup label="Box Style" settingKey="spaceSpiralBoxStyle" disabled={!isSpaceSpiral} disabledMessage={spaceSpiralMsg} modes={['SPACE_SPIRAL']} darkMode={config.darkMode}>
                        <select 
                            value={config.spaceSpiralBoxStyle || 'MATTE'} 
                            onChange={(e) => setConfig(p => ({...p, spaceSpiralBoxStyle: e.target.value}))}
                            className={`w-full h-11 px-4 rounded-xl text-[10px] font-black uppercase tracking-widest outline-none border transition-all ${config.darkMode ? 'bg-black/20 border-white/10 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
                        >
                            <option value="NONE">None</option>
                            <option value="MATTE">Matte</option>
                            <option value="SHINY">Shiny</option>
                            <option value="NEON">Neon</option>
                            <option value="CHROME">Chrome</option>
                            <option value="METAL">Metal</option>
                            <option value="GHOST">Ghost</option>
                            <option value="CRYSTAL">Crystal</option>
                            <option value="GLOW">Glow</option>
                            <option value="WIREFRAME">Wireframe</option>
                            <option value="HOLOGRAPHIC">Holographic</option>
                            <option value="PLASMA">Plasma</option>
                        </select>
                    </ControlGroup>

                    <ControlGroup label="Texture" settingKey="spaceSpiralBoxTexture" disabled={!isSpaceSpiral} disabledMessage={spaceSpiralMsg} modes={['SPACE_SPIRAL']} darkMode={config.darkMode}>
                        <select 
                            value={config.spaceSpiralBoxTexture || 'NONE'} 
                            onChange={(e) => setConfig(p => ({...p, spaceSpiralBoxTexture: e.target.value}))}
                            className={`w-full h-11 px-4 rounded-xl text-[10px] font-black uppercase tracking-widest outline-none border transition-all ${config.darkMode ? 'bg-black/20 border-white/10 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
                        >
                            <option value="NONE">None</option>
                            <option value="STRIPES">Stripes</option>
                            <option value="DOTS">Dots</option>
                            <option value="CROSSHATCH">Crosshatch</option>
                            <option value="NOISE">Noise</option>
                            <option value="CHECKERBOARD">Checkerboard</option>
                            <option value="HONEYCOMB">Honeycomb</option>
                            <option value="CIRCUIT">Circuit</option>
                            <option value="WAVES">Waves</option>
                        </select>
                    </ControlGroup>
                </div>

                <ControlGroup label="Color Engine" settingKey="spaceSpiralColorMode" disabled={!isSpaceSpiral} disabledMessage={spaceSpiralMsg} modes={['SPACE_SPIRAL']} darkMode={config.darkMode}>
                    <select 
                        value={config.spaceSpiralColorMode || 'RAINBOW'} 
                        onChange={(e) => setConfig(p => ({...p, spaceSpiralColorMode: e.target.value as any}))}
                        className={`w-full h-11 px-4 rounded-xl text-[10px] font-black uppercase tracking-widest outline-none border transition-all ${config.darkMode ? 'bg-black/20 border-white/10 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
                    >
                        <option value="SINGLE">Single Color</option>
                        <option value="ALTERNATING">Alternating</option>
                        <option value="RAINBOW">Rainbow</option>
                        <option value="GRADIENT">Gradient Flux</option>
                    </select>
                </ControlGroup>

                <div className="grid grid-cols-2 gap-4">
                    {(config.spaceSpiralColorMode === 'SINGLE' || config.spaceSpiralColorMode === 'ALTERNATING' || config.spaceSpiralColorMode === 'GRADIENT') && (
                        <ControlGroup label="Color A" settingKey="spaceSpiralColorA" disabled={!isSpaceSpiral} disabledMessage={spaceSpiralMsg} modes={['SPACE_SPIRAL']} darkMode={config.darkMode}>
                             <div className="flex items-center gap-3">
                                <input 
                                    type="color" 
                                    value={config.spaceSpiralColorA || '#3b82f6'} 
                                    onChange={(e) => setConfig(p => ({...p, spaceSpiralColorA: e.target.value}))}
                                    className="w-10 h-10 rounded-lg cursor-pointer border-2 border-white/10 shrink-0"
                                />
                                <span className="text-[8px] font-mono opacity-40">{config.spaceSpiralColorA?.toUpperCase()}</span>
                             </div>
                        </ControlGroup>
                    )}
                    
                    {(config.spaceSpiralColorMode === 'ALTERNATING' || config.spaceSpiralColorMode === 'GRADIENT') && (
                        <ControlGroup label="Color B" settingKey="spaceSpiralColorB" disabled={!isSpaceSpiral} disabledMessage={spaceSpiralMsg} modes={['SPACE_SPIRAL']} darkMode={config.darkMode}>
                            <div className="flex items-center gap-3">
                                <input 
                                    type="color" 
                                    value={config.spaceSpiralColorB || '#ef4444'} 
                                    onChange={(e) => setConfig(p => ({...p, spaceSpiralColorB: e.target.value}))}
                                    className="w-10 h-10 rounded-lg cursor-pointer border-2 border-white/10 shrink-0"
                                />
                                <span className="text-[8px] font-mono opacity-40">{config.spaceSpiralColorB?.toUpperCase()}</span>
                             </div>
                        </ControlGroup>
                    )}
                </div>

                <div className="grid grid-cols-2 gap-4 border-t border-white/5 pt-4">
                    <SmartInput label="Opacity" settingKey="spaceSpiralBoxFillOpacity" min={0} max={1} step={0.1} value={config.spaceSpiralBoxFillOpacity ?? 1.0} onChange={v => setConfig(p => ({...p, spaceSpiralBoxFillOpacity: v}))} darkMode={config.darkMode} disabled={!isSpaceSpiral} />
                    <SmartInput label="Texture Opacity" settingKey="spaceSpiralBoxTextureOpacity" min={0} max={1} step={0.1} value={config.spaceSpiralBoxTextureOpacity ?? 0.5} onChange={v => setConfig(p => ({...p, spaceSpiralBoxTextureOpacity: v}))} darkMode={config.darkMode} disabled={!isSpaceSpiral} />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <SmartInput label="Glow Radius" settingKey="spaceSpiralBoxGlowRadius" min={0} max={100} value={config.spaceSpiralBoxGlowRadius ?? 0} onChange={v => setConfig(p => ({...p, spaceSpiralBoxGlowRadius: v}))} darkMode={config.darkMode} disabled={!isSpaceSpiral} />
                    <ControlGroup label="Glow Color" settingKey="spaceSpiralBoxGlowColor" disabled={!isSpaceSpiral} darkMode={config.darkMode}>
                        <input 
                            type="color" 
                            value={config.spaceSpiralBoxGlowColor || '#ffffff'} 
                            onChange={(e) => setConfig(p => ({...p, spaceSpiralBoxGlowColor: e.target.value}))}
                            className="w-full h-8 rounded-lg cursor-pointer border border-white/10"
                        />
                    </ControlGroup>
                </div>

                <div className="grid grid-cols-2 gap-4 border-t border-white/5 pt-4">
                    <ControlGroup label="Border Color" settingKey="spaceSpiralBoxBorderColor" disabled={!isSpaceSpiral} disabledMessage={spaceSpiralMsg} modes={['SPACE_SPIRAL']} darkMode={config.darkMode}>
                        <input 
                            type="color" 
                            value={config.spaceSpiralBoxBorderColor || '#ffffff'} 
                            onChange={(e) => setConfig(p => ({...p, spaceSpiralBoxBorderColor: e.target.value}))}
                            className="w-full h-8 rounded-lg cursor-pointer border border-white/10"
                        />
                    </ControlGroup>
                    <SmartInput label="Edge Thickness" settingKey="spaceSpiralBoxBorderWidth" min={0} max={10} value={config.spaceSpiralBoxBorderWidth || 0} onChange={v => setConfig(p => ({...p, spaceSpiralBoxBorderWidth: v}))} darkMode={config.darkMode} disabled={!isSpaceSpiral} disabledMessage={spaceSpiralMsg} modes={['SPACE_SPIRAL']} />
                </div>
            </div>
        </div>

        {/* BALL PHYSICS */}
        <div className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
                <Settings2 className="w-3.5 h-3.5 opacity-20" />
                <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">Kinematics</h4>
                <InfoPopup settingKey="spaceSpiralKinematics" darkMode={config.darkMode} />
            </div>
            
            <div className="flex flex-col gap-4">
                <div className="grid grid-cols-2 gap-4">
                    <SmartInput label="Ball Mass" settingKey="spaceSpiralMassBall" min={0.1} max={100} value={config.spaceSpiralMassBall} onChange={v => setConfig(p => ({...p, spaceSpiralMassBall: v}))} darkMode={config.darkMode} disabled={!isSpaceSpiral} disabledMessage={spaceSpiralMsg} modes={['SPACE_SPIRAL']} />
                    <SmartInput label="Box Mass" settingKey="spaceSpiralMassBox" min={1} max={1000} value={config.spaceSpiralMassBox} onChange={v => setConfig(p => ({...p, spaceSpiralMassBox: v}))} darkMode={config.darkMode} disabled={!isSpaceSpiral} disabledMessage={spaceSpiralMsg} modes={['SPACE_SPIRAL']} />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <SmartInput label="Ball Bounce" settingKey="spaceSpiralRestitution" min={0} max={1.5} step={0.05} value={config.spaceSpiralRestitution ?? 0.8} onChange={v => setConfig(p => ({...p, spaceSpiralRestitution: v}))} darkMode={config.darkMode} disabled={!isSpaceSpiral} disabledMessage={spaceSpiralMsg} modes={['SPACE_SPIRAL']} />
                    <SmartInput label="Box Bounce" settingKey="spaceSpiralBoxRestitution" min={0} max={1.5} step={0.05} value={config.spaceSpiralBoxRestitution ?? 1.0} onChange={v => setConfig(p => ({...p, spaceSpiralBoxRestitution: v}))} darkMode={config.darkMode} disabled={!isSpaceSpiral} disabledMessage={spaceSpiralMsg} modes={['SPACE_SPIRAL']} />
                </div>
            </div>
        </div>

        {/* SOUNDS */}
        <div className="space-y-4 p-5 rounded-3xl border border-zinc-500/10 bg-black/5">
            <div className="flex items-center gap-2 mb-2">
                <Volume2 className="w-3.5 h-3.5 opacity-40" />
                <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">Sonic Pulse</h4>
                <InfoPopup settingKey="spaceSpiralSounds" darkMode={config.darkMode} />
            </div>
            
            <div className="flex flex-col gap-4">
                <ControlGroup label="Bounce Effect" settingKey="spaceSpiralSoundBounce" disabled={!isSpaceSpiral} disabledMessage={spaceSpiralMsg} modes={['SPACE_SPIRAL']} darkMode={config.darkMode}>
                    <select 
                        value={config.spaceSpiralSoundBounce || 'Wood'} 
                        onChange={(e) => setConfig(p => ({...p, spaceSpiralSoundBounce: e.target.value}))}
                        className={`w-full h-11 px-4 rounded-xl text-[10px] font-black uppercase tracking-widest outline-none border transition-all ${config.darkMode ? 'bg-black/20 border-white/10 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
                    >
                        {BALL_SOUND_EFFECTS.map(s => <option key={s} value={s}>{s}</option>)}
                        {config.userSounds && config.userSounds.map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                    </select>
                </ControlGroup>
            </div>
        </div>

        {/* SEQUENCE PLANNER */}
        <div className={`space-y-4 ${isLockedFn('spaceSpiralSequence') ? 'opacity-50 grayscale pointer-events-none' : ''}`}>
            <div className="flex items-center justify-between px-1">
                <div className="flex flex-col items-start gap-1">
                    <div className="flex items-center gap-2">
                        <Database className="w-3.5 h-3.5 opacity-50 text-cyan-500" />
                        <span className="text-[10px] font-black uppercase tracking-widest leading-none">Round Sequence Planner</span>
                        <InfoPopup settingKey="spaceSpiralSequence" darkMode={config.darkMode} />
                    </div>
                    {isLockedFn('spaceSpiralSequence') && <div className="text-[8px] font-bold text-amber-500 uppercase tracking-tighter">Requires {getLockInfo?.('spaceSpiralSequence').min_plan || 'Elite'} License</div>}
                </div>
                
                <Toggle active={config.spaceSpiralEnableRounds} onClick={() => setConfig(p => ({...p, spaceSpiralEnableRounds: !p.spaceSpiralEnableRounds}))} />
            </div>

            {config.spaceSpiralEnableRounds && (
                <div className={`space-y-4 animate-in slide-in-from-top-4 duration-300 ${!isSpaceSpiral ? 'opacity-30 grayscale pointer-events-none' : ''}`}>
                    <SmartInput label="Interval (sec)" settingKey="spaceSpiralRoundTimeLimit" min={5} h-11 max={300} step={5} value={config.spaceSpiralRoundTimeLimit} onChange={v => setConfig(p => ({...p, spaceSpiralRoundTimeLimit: v}))} darkMode={config.darkMode} disabled={!isSpaceSpiral} disabledMessage={spaceSpiralMsg} modes={['SPACE_SPIRAL']} />
                    
                    <button 
                        onClick={() => setConfig(p => ({
                            ...p, 
                            spaceSpiralRoundConfigs: [
                                ...(p.spaceSpiralRoundConfigs || []),
                                {
                                    id: Math.random().toString(),
                                    massBox: p.spaceSpiralMassBox,
                                    forwardThrust: p.spaceSpiralForwardThrust || 0,
                                    thrustDuration: p.spaceSpiralThrustDuration || 0,
                                    thrustAngle: p.spaceSpiralThrustAngle || 0,
                                    gravityPower: p.spaceSpiralGravityPower || 1.0,
                                    airResistance: p.spaceSpiralAirResistance || 0.99,
                                    restitution: p.spaceSpiralRestitution || 0.5,
                                    boxRestitution: p.spaceSpiralBoxRestitution || 1.0
                                }
                            ]
                        }))}
                        className="w-full py-4 rounded-xl border border-dashed border-cyan-500/30 text-[10px] font-black text-cyan-500 hover:bg-cyan-500/5 transition-all uppercase tracking-widest flex items-center justify-center gap-2"
                    >
                        <i className="fa-solid fa-plus"></i>
                        Add New Protocol Round
                    </button>

                    <div className="flex flex-col gap-3 max-h-[400px] overflow-y-auto custom-scrollbar p-1">
                        {(config.spaceSpiralRoundConfigs || []).map((round, idx) => (
                            <div key={round.id} className={`flex flex-col gap-4 p-5 rounded-3xl border ${config.darkMode ? 'bg-black/40 border-white/5' : 'bg-white border-neutral-200 shadow-lg shadow-black/5'}`}>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-xl bg-cyan-600 text-white flex items-center justify-center text-[11px] font-black shadow-lg shadow-cyan-600/20">
                                            {idx + 1}
                                        </div>
                                        <span className="text-[10px] font-black uppercase tracking-widest">Protocol Round {idx + 1}</span>
                                    </div>
                                    <button 
                                        onClick={() => setConfig(p => ({
                                            ...p, 
                                            spaceSpiralRoundConfigs: p.spaceSpiralRoundConfigs.filter(r => r.id !== round.id)
                                        }))}
                                        className="w-8 h-8 rounded-lg hover:bg-red-500/10 text-red-500 transition-all flex items-center justify-center"
                                    >
                                        <i className="fa-solid fa-trash text-[12px]"></i>
                                    </button>
                                </div>
                                
                                <div className="grid grid-cols-1 gap-4">
                                    <SmartInput label="Box Mass" min={1} max={1000} value={round.massBox} onChange={v => setConfig(p => ({...p, spaceSpiralRoundConfigs: p.spaceSpiralRoundConfigs.map(r => r.id === round.id ? {...r, massBox: v} : r)}))} darkMode={config.darkMode} />
                                    <SmartInput label="Forward Thrust" min={0} max={50} step={0.5} value={round.forwardThrust} onChange={v => setConfig(p => ({...p, spaceSpiralRoundConfigs: p.spaceSpiralRoundConfigs.map(r => r.id === round.id ? {...r, forwardThrust: v} : r)}))} darkMode={config.darkMode} />
                                    <SmartInput label="Gravity Power" min={0} max={5} step={0.1} value={round.gravityPower} onChange={v => setConfig(p => ({...p, spaceSpiralRoundConfigs: p.spaceSpiralRoundConfigs.map(r => r.id === round.id ? {...r, gravityPower: v} : r)}))} darkMode={config.darkMode} />
                                </div>
                            </div>
                        ))}
                        {(config.spaceSpiralRoundConfigs || []).length === 0 && (
                            <div className="text-center py-10 rounded-2xl border-2 border-dashed border-zinc-500/10 text-[10px] font-medium opacity-30 italic">No rounds added. Use the sequence planner to create dynamic multi-stage simulations.</div>
                        )}
                    </div>
                </div>
            )}
        </div>
      </div>
    </CollapsibleSection>
  );
};
